// packages/contracts/src/bill-reference.ts
import { z } from 'zod'

/**
 * The two bill references, which are both opaque strings and are NOT the same
 * thing.
 *
 * ## Why this is a type and not a `string`
 *
 * A bill arriving on the file-exchange path carries the SUBMITTER's own
 * reference — the id in the Healthcare Service Provider's billing system — and
 * PRS assigns its own. Both are opaque, both look like `SGH-2026-000417`, and
 * confusing them is silent in every layer a human checks:
 *
 *  - `get(scope, hspsOwnReference)` returns nothing, which is indistinguishable
 *    from "you may not see that bill" by construction (that 404 is deliberate —
 *    it is what stops a refusal being an enumeration oracle), so the caller sees
 *    a plausible refusal rather than a defect.
 *  - the reverse — rendering PRS's reference into an outbound file where the
 *    HSP's belongs — produces a file the counterparty cannot reconcile, and it
 *    reconciles perfectly on our side.
 *
 * Neither is caught by a test written with one agency's fixtures, because in a
 * fixture the two are usually generated from the same seed string.
 *
 * ## The bounds are OURS
 *
 * ⚠ P2C11 §14.6(b)(c)(d) makes type, length and syntax validation MANDATORY,
 * server-side, for all input the application processes. It names no value.
 * P2C3 §7.1.2 delegates data element formats to a Whole-of-Government data
 * standard, and its parent §7.1.1 keeps that standard behind an on-site viewing
 * undertaking at the Authority's premises — so it is not in the pack and cannot
 * be. The bounds below therefore discharge a real obligation against a standard
 * we have not read, exactly as `010_reject_reason_length.sql` records for the
 * rejection reason (A-11 / Q-18), and registered for this whole published interface as A-22. `TODO(WOG-DATA-STD)`.
 *
 * ⚠ The excluded characters are not decoration. The exchanged file is
 * line-oriented and delimited (see `services/transfer`), and a reference
 * containing the delimiter, a line terminator or a control character would
 * either split one record into two or corrupt the outcome file this same
 * reference is rendered back into. Refusing it is a per-record rejection with a
 * reason the sender can act on; accepting it is a file the sender cannot parse.
 */
declare const billReferenceBrand: unique symbol
declare const sourceBillReferenceBrand: unique symbol

/** Longest reference either side may use. Ours — see the header. */
export const MAX_BILL_REFERENCE_LENGTH = 64

/**
 * Printable, no whitespace, no delimiter, no control character, and it must
 * start with a letter or a digit.
 *
 * A VALUE compared with a regular expression, never SQL that is concatenated,
 * so it cannot be an injection route however it was written.
 */
const REFERENCE_SYNTAX = /^[A-Za-z0-9][A-Za-z0-9._:/-]*$/

const reference = z
  .string()
  .min(1, 'must not be empty')
  .max(MAX_BILL_REFERENCE_LENGTH, `must be at most ${MAX_BILL_REFERENCE_LENGTH} characters (P2C11 §14.6(c))`)
  .regex(
    REFERENCE_SYNTAX,
    'must start with a letter or digit and contain only letters, digits and . _ : / - ' +
      '(P2C11 §14.6(d); the exchanged file is delimited, so a delimiter or line break in a ' +
      'reference would split one record into two)',
  )

/** PRS's own reference for a bill. The primary key of `app.bill`. */
export const BillReference = reference.brand<typeof billReferenceBrand>()
export type BillReference = z.infer<typeof BillReference>

/**
 * The SUBMITTER's own reference, as it arrived in the file.
 *
 * ⚠ This is the value `UNIQUE (hsp_agency_code, source_reference)` is built on,
 * and that uniqueness is the load-bearing duplicate guard for the whole intake:
 * it survives a re-send with different line endings, a re-ordered file, or the
 * same bill split across two days' deliveries — none of which a digest over the
 * bytes can see.
 */
export const SourceBillReference = reference.brand<typeof sourceBillReferenceBrand>()
export type SourceBillReference = z.infer<typeof SourceBillReference>

/**
 * Parse PRS's own bill reference from a raw string.
 *
 * ⚠ The conditional type is not decoration — it is the same control
 * `serviceDate()` carries and for the same recorded reason. A branded string is
 * still assignable to `string`, so a plain `(value: string)` signature accepts a
 * `SourceBillReference` without a cast: `billReference(record.sourceReference)`
 * would compile, validate (it is a well-formed reference), and reach every
 * lookup with nothing for a reviewer to see. That laundering route survived
 * eight negative tests on `ServiceDate` because all of them probed ASSIGNMENT
 * rather than the constructor, and the fix was this signature.
 */
export const billReference = <T extends string>(
  value: T & (T extends SourceBillReference ? never : T),
): BillReference => BillReference.parse(value)

/** The mirror: PRS's own reference must not launder into a submitter's. */
export const sourceBillReference = <T extends string>(
  value: T & (T extends BillReference ? never : T),
): SourceBillReference => SourceBillReference.parse(value)
